import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from "@chakra-ui/react";
import React from "react";
import { FieldPath, FieldValues, get, UseFormReturn } from "react-hook-form";

type FormFieldControlProps<TFormValues extends FieldValues> = {
  form: UseFormReturn<TFormValues>;
  name: FieldPath<TFormValues>;
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  isRequired?: boolean;
  isDisabled?: boolean;
  children: React.ReactNode;
};

/**
 * Connects react-hook-form state to Chakra UI's form field styling.
 *
 * The input itself still needs to be registered or controlled separately.
 * This component only handles the shared label, helper text, error message,
 * required, disabled, and invalid states.
 */
export function FormFieldControl<T extends FieldValues>({
  form,
  name,
  label,
  helperText,
  isRequired,
  isDisabled,
  children,
}: FormFieldControlProps<T>) {
  const error = get(form.formState.errors, name);
  const errorMessage = error?.message ? String(error.message) : undefined;

  // convert nested fields with dot notation to a valid id string
  const fieldId = String(name).replace(/\./g, "-");

  // Prevent the form from being in the invalid state while submitting, to
  // prevent validation errors flashing after submit.
  // e.g. if the input is active, and you press the "Enter" key to submit the form.
  // We force all inputs to become 'isDisabled' as we want to style and prevent
  // interaction while submitting. However, react-hook-form treats inputs which
  // are disabled as having an `undefined` value. The `undefined` value can then
  // cause validation errors, e.g. if the input isRequired.
  const isInvalid = !!error && !form.formState.isSubmitting;

  return (
    <FormControl
      id={fieldId}
      isRequired={isRequired}
      isDisabled={isDisabled || form.formState.isSubmitting}
      isInvalid={isInvalid}
    >
      {label && <FormLabel>{label}</FormLabel>}

      {children}

      {!isInvalid && helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}

      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
}
